import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { horseListService } from "../api";
import { formatDate } from "../api/formatDate";
import "../assets/styles/pages/lists-transactions.scss";
import MainStructure from "../components/containers/MainStructure";
import Pagination from "../components/elements/Pagination";
import SearchInput from "../components/elements/SearchInput";
import SourAlert from "../components/elements/SourAlert";
import useSourAlert from "../hooks/useSourAlert";

const PAGE_SIZE = 10;

export function getListUpdatedAt(list) {
	return list.modified || list.created;
}

export function filterAndSortLists(lists, searchQuery, sortBy) {
	const query = searchQuery.trim().toLowerCase();
	const filtered = lists.filter((list) => {
		if (!query) return true;
		return [list.name, list.type]
			.filter(Boolean)
			.some((value) => String(value).toLowerCase().includes(query));
	});

	return [...filtered].sort((first, second) => {
		if (sortBy === "name") {
			return String(first.name || "").localeCompare(String(second.name || ""));
		}
		if (sortBy === "horses") {
			return (Number(second.total) || 0) - (Number(first.total) || 0);
		}
		const firstDate = new Date(getListUpdatedAt(first)).getTime() || 0;
		const secondDate = new Date(getListUpdatedAt(second)).getTime() || 0;
		return sortBy === "oldest" ? firstDate - secondDate : secondDate - firstDate;
	});
}

function getListStatus(list) {
	if (list.state === "completed") return { label: "Ready", className: "is-ready" };
	if (list.state === "error") return { label: "Import failed", className: "is-error" };
	return { label: "Importing", className: "is-processing" };
}

function MyLists() {
	const [lists, setLists] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [sortBy, setSortBy] = useState("newest");
	const [currentPage, setCurrentPage] = useState(1);
	const [renamingId, setRenamingId] = useState(null);
	const [renameValue, setRenameValue] = useState("");
	const [busyAction, setBusyAction] = useState("");
	const [alertProps, showAlert] = useSourAlert();

	const loadLists = useCallback(() => {
		setLoading(true);
		setLoadError(false);
		return horseListService
			.list("?limit=10000&offset=0")
			.then((response) => setLists(response?.results || []))
			.catch(() => setLoadError(true))
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		loadLists();
	}, [loadLists]);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery, sortBy]);

	const visibleLists = useMemo(
		() => filterAndSortLists(lists, searchQuery, sortBy),
		[lists, searchQuery, sortBy]
	);
	const totalPages = Math.ceil(visibleLists.length / PAGE_SIZE);
	const pageStart = (currentPage - 1) * PAGE_SIZE;
	const pageLists = visibleLists.slice(pageStart, pageStart + PAGE_SIZE);
	const horseCount = lists.reduce((total, list) => total + (Number(list.total) || 0), 0);

	useEffect(() => {
		if (totalPages && currentPage > totalPages) setCurrentPage(totalPages);
	}, [currentPage, totalPages]);

	function startRename(list) {
		setRenamingId(list.id);
		setRenameValue(list.name || "");
	}

	function handleRename(list) {
		const nextName = renameValue.trim();
		if (!nextName || nextName === list.name) {
			setRenamingId(null);
			return;
		}
		setBusyAction(`rename-${list.id}`);
		horseListService
			.update(list.id, { name: nextName })
			.then(() => {
				setLists((previous) => previous.map((item) => (
					item.id === list.id
						? { ...item, name: nextName, modified: new Date().toISOString() }
						: item
				)));
				setRenamingId(null);
			})
			.catch(() => showAlert({
				type: "error",
				title: "List not renamed",
				description: "We could not rename this list. Please try again.",
				okButtonText: "OK",
			}))
			.finally(() => setBusyAction(""));
	}

	function handleDuplicate(list) {
		setBusyAction(`duplicate-${list.id}`);
		horseListService
			.listHorses(list.id, "?limit=10000&offset=0")
			.then((response) => {
				const horseIds = (response?.results || []).map((horse) => horse.id);
				return horseListService.create({
					name: `Copy of ${list.name}`,
					type: list.type,
					horses: horseIds,
				}).then((created) => ({ created, horseCount: horseIds.length }));
			})
			.then(({ created, horseCount: copiedHorseCount }) => {
				const now = new Date().toISOString();
				setLists((previous) => [{
					...created,
					total: copiedHorseCount,
					created: created?.created || now,
					modified: created?.modified || created?.created || now,
				}, ...previous]);
				showAlert({
					type: "success",
					title: "List duplicated",
					description: `“${list.name}” was copied with ${copiedHorseCount} horse${copiedHorseCount === 1 ? "" : "s"}.`,
					okButtonText: "Done",
				});
			})
			.catch(() => showAlert({
				type: "error",
				title: "List not duplicated",
				description: "We could not duplicate this list. Please try again.",
				okButtonText: "OK",
			}))
			.finally(() => setBusyAction(""));
	}

	function handleDelete(list) {
		showAlert({
			type: "warning",
			title: "Delete list?",
			description: `“${list.name}” will be permanently removed. This cannot be undone.`,
			showCancelButton: true,
			cancelButtonText: "Keep list",
			showOkButton: true,
			okButtonText: "Delete list",
			onOk: () => {
				setBusyAction(`delete-${list.id}`);
				horseListService
					.remove(list.id)
					.then(() => setLists((previous) => previous.filter((item) => item.id !== list.id)))
					.catch(() => showAlert({
						type: "error",
						title: "List not deleted",
						description: "We could not delete this list. Please try again.",
						okButtonText: "OK",
					}))
					.finally(() => setBusyAction(""));
			},
		});
	}

	return (
		<MainStructure>
			<main className="records-page lists-page">
				<header className="records-page__header">
					<div>
						<p className="records-page__eyebrow">Horse collections</p>
						<h1>My Lists</h1>
						<p>Organise sires and dams into reusable lists for faster multi-report setup.</p>
					</div>
					<Link to="/new-list" className="records-primary-action">
						<i className="fa fa-plus" aria-hidden="true" /> Create list
					</Link>
				</header>

				<div className="records-summary" aria-label="List summary">
					<div><i className="fa fa-list" aria-hidden="true" /><span><strong>{lists.length}</strong> lists</span></div>
					<div><i className="fa fa-horse" aria-hidden="true" /><span><strong>{horseCount}</strong> horses organised</span></div>
				</div>

				<section className="records-panel" aria-labelledby="my-lists-title">
					<div className="records-toolbar">
						<div>
							<h2 id="my-lists-title">Saved lists</h2>
							<p>{visibleLists.length} {visibleLists.length === 1 ? "list" : "lists"} found</p>
						</div>
						<div className="records-toolbar__controls">
							<SearchInput
								value={searchQuery}
								onChange={(event) => setSearchQuery(event.target.value)}
								placeholder="Search name or type"
								ariaLabel="Search horse lists"
							/>
							<label className="records-sort">
								<span>Sort</span>
								<select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
									<option value="newest">Recently updated</option>
									<option value="oldest">Oldest updated</option>
									<option value="name">Name A–Z</option>
									<option value="horses">Most horses</option>
								</select>
							</label>
						</div>
					</div>

					{loading ? (
						<div className="records-state" role="status"><span className="records-spinner" /> Loading your lists…</div>
					) : loadError ? (
						<div className="records-state is-error">
							<i className="fa fa-exclamation-circle" aria-hidden="true" />
							<h3>Lists could not be loaded</h3>
							<p>Check your connection and try again.</p>
							<button type="button" onClick={loadLists}>Try again</button>
						</div>
					) : pageLists.length ? (
						<>
							<div className="records-table-meta">
								<span>{pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, visibleLists.length)} of {visibleLists.length}</span>
							</div>
							<div className="records-table-wrap">
								<table className="records-table lists-table">
									<thead><tr><th>List</th><th>Type</th><th>Horses</th><th>Last updated</th><th>Status</th><th><span className="visually-hidden">Actions</span></th></tr></thead>
									<tbody>
										{pageLists.map((list) => {
											const status = getListStatus(list);
											const isReady = list.state === "completed";
											const isBusy = busyAction.endsWith(`-${list.id}`);
											return (
												<tr key={list.id}>
													<td data-label="List">
														{renamingId === list.id ? (
															<div className="list-rename">
																<input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} aria-label={`New name for ${list.name}`} autoFocus />
																<button type="button" onClick={() => handleRename(list)} disabled={isBusy}>Save</button>
																<button type="button" onClick={() => setRenamingId(null)}>Cancel</button>
															</div>
														) : <strong>{list.name}</strong>}
														{list.preloaded ? <span className="record-note">Shared list</span> : null}
													</td>
													<td data-label="Type"><span className={`record-type is-${list.type}`}>{list.type || "—"}</span></td>
													<td data-label="Horses"><strong>{list.total ?? 0}</strong></td>
													<td data-label="Last updated">{formatDate(getListUpdatedAt(list))}</td>
													<td data-label="Status"><span className={`record-status ${status.className}`}>{status.label}</span></td>
													<td data-label="Actions">
														<div className="record-actions">
															{isReady && !list.preloaded ? <Link to={`/edit-list/${list.id}`}><i className="fa fa-folder-open" aria-hidden="true" /> Open</Link> : null}
															{isReady && !list.preloaded ? <button type="button" onClick={() => startRename(list)} disabled={isBusy}><i className="fa fa-pen" aria-hidden="true" /> Rename</button> : null}
															{isReady ? <button type="button" onClick={() => handleDuplicate(list)} disabled={isBusy}><i className="fa fa-copy" aria-hidden="true" /> {busyAction === `duplicate-${list.id}` ? "Copying…" : "Duplicate"}</button> : null}
															{!list.preloaded ? <button type="button" className="is-danger" onClick={() => handleDelete(list)} disabled={isBusy}><i className="fa fa-trash" aria-hidden="true" /> Delete</button> : null}
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							{totalPages > 1 ? <div className="records-pagination"><Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} /></div> : null}
						</>
					) : (
						<div className="records-state">
							<i className="fa fa-list" aria-hidden="true" />
							<h3>{searchQuery ? "No matching lists" : "Create your first horse list"}</h3>
							<p>{searchQuery ? "Try a different name or list type." : "Save sires or dams together so they are ready for future reports."}</p>
							{searchQuery ? <button type="button" onClick={() => setSearchQuery("")}>Clear search</button> : <Link to="/new-list">Create list</Link>}
						</div>
					)}
				</section>
			</main>
			<SourAlert {...alertProps} />
		</MainStructure>
	);
}

export default MyLists;
